import { useCallback, useEffect, useRef, useState } from 'react';
import { type TFunction } from 'i18next';
import { useApi } from '../contexts/ApiContext';

export interface InvoicePreview {
    invoiceNumber: string;
    url: string | null;
}

export function useInvoicePreview(t: TFunction) {
    const api = useApi();
    const [invoicePreview, setInvoicePreview] = useState<InvoicePreview | null>(null);
    const [invoicePreviewLoading, setInvoicePreviewLoading] = useState(false);
    const [invoicePreviewError, setInvoicePreviewError] = useState<string | null>(null);
    const invoicePreviewUrlRef = useRef<string | null>(null);
    const invoicePreviewRequestIdRef = useRef(0);

    const revokeInvoicePreviewUrl = useCallback(() => {
        if (invoicePreviewUrlRef.current) {
            URL.revokeObjectURL(invoicePreviewUrlRef.current);
            invoicePreviewUrlRef.current = null;
        }
    }, []);

    const closeInvoicePreview = useCallback(() => {
        invoicePreviewRequestIdRef.current += 1;
        revokeInvoicePreviewUrl();
        setInvoicePreview(null);
        setInvoicePreviewError(null);
        setInvoicePreviewLoading(false);
    }, [revokeInvoicePreviewUrl]);

    useEffect(() => revokeInvoicePreviewUrl, [revokeInvoicePreviewUrl]);

    const showInvoicePreview = useCallback(
        async (invoiceNumber: string, errorKey: string, documentType?: string) => {
            const requestId = invoicePreviewRequestIdRef.current + 1;
            invoicePreviewRequestIdRef.current = requestId;
            revokeInvoicePreviewUrl();
            setInvoicePreview({ invoiceNumber, url: null });
            setInvoicePreviewError(null);
            setInvoicePreviewLoading(true);

            try {
                const result = await api.invoiceDocument.previewByInvoiceNumber(
                    invoiceNumber,
                    undefined,
                    documentType,
                );
                if (requestId !== invoicePreviewRequestIdRef.current) return;

                if (!result.success || !result.data) {
                    setInvoicePreviewError(result.error ?? t(errorKey));
                    return;
                }

                const url = URL.createObjectURL(result.data);
                invoicePreviewUrlRef.current = url;
                setInvoicePreview({ invoiceNumber, url });
            } catch (error) {
                if (requestId === invoicePreviewRequestIdRef.current) {
                    console.error('Show invoice preview error:', error);
                    setInvoicePreviewError(t(errorKey));
                }
            } finally {
                if (requestId === invoicePreviewRequestIdRef.current) {
                    setInvoicePreviewLoading(false);
                }
            }
        },
        [api.invoiceDocument, revokeInvoicePreviewUrl, t],
    );

    return {
        invoicePreview,
        setInvoicePreview,
        invoicePreviewLoading,
        setInvoicePreviewLoading,
        invoicePreviewError,
        setInvoicePreviewError,
        invoicePreviewUrlRef,
        showInvoicePreview,
        closeInvoicePreview,
    };
}
